import i18n from '~shared/utils/i18n';
import { isBetfeedPageEnabled, isBetfeedWidgetEnabled } from '~scenes/betfeed/app/utils';
import { getVirtualGames, getVirtualGameBySlug } from '~shared/services/virtuals';
import { getSportsPageSeoUrl } from '../../Utils/SeoUrls';
import { getState } from '~shared/utils/state';
import { ALL_CAROUSEL_ITEMS, SPORT_PAGE_TABS } from './constants';
import { VIRTUAL_GENERIC_SLUG_MAPPINGS } from '~spa/Pages/VirtualSports/constants';
import { SPORTS_WITH_OWN_LAYOUT } from '~shared/utils/constants';

/**
 * Filter the list of carousel items in case, e.g. a virtual game is disabled
 * @param {String} sportSlug the sport
 * @param {number} sportId sport id
 * @returns {Object} the filtered list
 */
export const filterCarouselItems = async (sportSlug, sportId) => {
    const filteredItems = { ...ALL_CAROUSEL_ITEMS };

    let virtuals = {};
    const virtualGames = await getVirtualGames();
    if (!virtualGames.length) {
        delete filteredItems[SPORT_PAGE_TABS.VIRTUALS];
    } else {
        const virtualGameSlug = VIRTUAL_GENERIC_SLUG_MAPPINGS[sportSlug];
        virtuals = getVirtualGameBySlug(virtualGames, virtualGameSlug);
        const noVirtuals = !Object.keys(virtuals).length;
        if (noVirtuals) {
            delete filteredItems[SPORT_PAGE_TABS.VIRTUALS];
        }
    }

    if (!isBetfeedWidgetEnabled() || !isBetfeedPageEnabled()) {
        delete filteredItems[SPORT_PAGE_TABS.POPULAR_BETS];
    }

    const carouselItems = Object.keys(filteredItems).map((slug) => {
        const display = i18n.t(`${filteredItems[slug].i18nKey}`);
        const url = getSportsPageSeoUrl(sportSlug, slug, sportId);

        return {
            display,
            slug,
            url,
        };
    });

    return {
        carouselItems,
        virtuals,
    };
};

/**
 * Check the sport is competition only layout (no carousel)
 * The sport with only competitions and outrights page
 * ex. athletics, politics, cycling, novelty-bets, surfing
 * @param {string} sportSlug sport slug
 * @returns {boolean} is it competition only layout?
 */
export const isGenericCompetitionsOnlySport = (sportSlug) => {
    const competitionsOnlySports = getState('competitions_only_sport_slugs') || [];

    return competitionsOnlySports.includes(sportSlug);
};

/**
 * Check the sport is multi grouping layout (no carousel)
 * The sport with only competitions and outrights page
 * The competition page will have betfeed on top, ex. motor-racing
 * @param {string} sportSlug sport slug
 * @returns {boolean} is it competition only layout?
 */
export const isGenericGroupingOnlySport = (sportSlug) => {
    const groupingOnlySports = getState('grouping_only_sport_slugs') || [];

    return groupingOnlySports.includes(sportSlug);
};

/**
 * 'Generic sport' is a terminology we are using to check the sport have
 * common layout (carousel with matches, Competitions, Odds Boots, Popular bets, Virtuals)
 * Currently the following sports excluded from generic sports
 *  a. sports has it's own layout (SPORTS_WITH_OWN_LAYOUT)
 *  b. competition only sports (athletics, politics, cycling, novelty-bets, surfing)
 *  c. grouping only sports (motor-racing)
 * Important Note: Competition only & grouping only sports also use the same components
 *  as generic sports but the layout is different (no carousel)
 * @param {string} sportSlug sport slug
 * @returns {boolean} does the sport is generic sport?
 */
export const isGenericSport = (sportSlug) => {
    return (
        !SPORTS_WITH_OWN_LAYOUT.includes(sportSlug) // sports with own layout has it's own logic
        && !isGenericCompetitionsOnlySport(sportSlug) // excluding competition only sports
        && !isGenericGroupingOnlySport(sportSlug) // excluding grouping only sports
    );
};
